/**
 * Dimensions panel — renders the estimate-level rollup as ranges with
 * confidence (Constitution III: never single-point).
 */

import type { Dimensions, DimensionRange } from "@starz/shared/api/types.gen";

interface Props {
  rollup: Dimensions | null | undefined;
  /** Hidden when no anchor is recorded. */
  hasAnchor: boolean;
}

const ROW_LABELS: Array<[keyof Dimensions, string]> = [
  ["floor_sqft", "Floor area"],
  ["wall_sqft", "Wall area"],
  ["ceiling_sqft", "Ceiling area"],
];

const CONF_LABEL: Record<DimensionRange["confidence_text"], string> = {
  low: "low confidence",
  medium: "medium confidence",
  high: "high confidence",
};

function formatSqft(d: DimensionRange): string {
  // "low – high sq ft (medium confidence)"
  const low = Math.round(d.low);
  const high = Math.round(d.high);
  return `${low.toLocaleString()} – ${high.toLocaleString()} sq ft`;
}

export function DimensionsPanel({ rollup, hasAnchor }: Props) {
  if (!hasAnchor) {
    return (
      <div className="rounded-xl border border-starz-gold/15 bg-starz-cream/40 p-5">
        <h3 className="font-serif text-xl text-starz-navy">Dimensions</h3>
        <p className="mt-2 text-sm text-warm-gray">
          Once you record a scale anchor (a known length), Stella will measure your space from the
          photos and show ranges here.
        </p>
      </div>
    );
  }

  const present = ROW_LABELS.filter(([k]) => rollup?.[k]);
  if (present.length === 0) {
    return (
      <div className="rounded-xl border border-starz-gold/15 bg-starz-cream/40 p-5">
        <h3 className="font-serif text-xl text-starz-navy">Dimensions</h3>
        <p className="mt-2 text-sm text-warm-gray">
          Measuring… give Stella a minute to process your photos.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-starz-gold/15 bg-white p-5 shadow-sm">
      <h3 className="font-serif text-xl text-starz-navy">Dimensions</h3>
      <p className="mt-1 text-xs text-warm-gray">
        Ranges, not single numbers — preliminary until your contractor reviews.
      </p>
      <dl className="mt-4 space-y-3">
        {present.map(([key, label]) => {
          const d = rollup![key]!;
          return (
            <div key={key} className="flex items-baseline justify-between gap-3">
              <dt className="text-sm font-medium text-starz-charcoal">{label}</dt>
              <dd className="text-right">
                <div className="text-sm text-starz-navy">{formatSqft(d)}</div>
                <div className="text-xs text-warm-gray">
                  {CONF_LABEL[d.confidence_text]}
                  {d.source === "admin_adjusted" ? " · adjusted by contractor" : ""}
                </div>
              </dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
}
